<template>
  <dialog
    :id="modalId"
    class="modal"
  >
    <div class="modal-box w-11/12 max-w-5xl rounded-[8px] bg-white text-black p-0 shadow-xl overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 class="font-bold text-lg">
          Manage {{ role }}s
        </h3>
        <button
          type="button"
          class="btn btn-sm bg-[#043f42] text-white hover:bg-[#043f42]/90 border-none rounded-[8px]"
          @click="addRow"
        >
          Add {{ role }}
        </button>
      </div>

      <!-- Body -->
      <div class="p-6 overflow-y-auto max-h-[60vh]">
        <div
          v-if="isLoading"
          class="flex justify-center items-center py-10"
        >
          <span class="loading loading-spinner loading-md text-[#043f42]" />
        </div>
        <div
          v-else-if="fetchError"
          class="text-red-500 text-center py-10"
        >
          {{ fetchError }}
        </div>
        <div v-else>
          <table class="table w-full">
            <thead>
              <tr class="text-gray-500 border-b border-gray-200">
                <th
                  v-for="col in columns"
                  :key="col"
                  class="font-medium bg-transparent px-2 py-3"
                >
                  {{ col }}
                </th>
                <th class="font-medium bg-transparent px-2 py-3 w-16" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in localParticipants"
                :key="index"
                class="border-b border-gray-100 hover:bg-gray-50"
              >
                <td
                  v-for="col in columns"
                  :key="col"
                  class="px-2 py-2"
                >
                  <input
                    v-model="row[columnKeys[col]]"
                    type="text"
                    class="input input-sm input-bordered w-full bg-white border-gray-300 focus:border-[#01d680] focus:ring-[#01d680] rounded-[6px]"
                  >
                </td>
                <td class="px-2 py-2 text-right">
                  <button
                    type="button"
                    class="text-red-500 hover:text-red-700 p-1"
                    @click="removeRow(index)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-if="localParticipants.length === 0"
            class="text-center text-gray-500 py-8"
          >
            No {{ role.toLowerCase() }}s found. Add one to get started.
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
        <form method="dialog">
          <button class="btn btn-sm bg-white text-[#043f42] border-[#043f42] hover:bg-gray-50 rounded-[8px]">
            Cancel
          </button>
        </form>
        <button
          type="button"
          :disabled="isSaving || isLoading"
          class="btn btn-sm bg-[#043f42] text-white hover:bg-[#043f42]/90 border-none rounded-[8px]"
          @click="save"
        >
          <span
            v-if="isSaving"
            class="loading loading-spinner loading-xs"
          />
          Save Changes
        </button>
      </div>
    </div>
    <form
      method="dialog"
      class="modal-backdrop"
    >
      <button>close</button>
    </form>
  </dialog>
</template>

<script>
export default {
  name: 'ManageParticipantsModal',
  props: {
    cohortId: {
      type: [String, Number],
      required: true
    },
    programmeId: {
      type: [String, Number],
      required: true
    },
    role: {
      type: String,
      required: true // 'Learner' or 'Sponsor'
    },
    modalId: {
      type: String,
      default: 'manage-participants-modal'
    }
  },
  data () {
    return {
      isLoading: false,
      isSaving: false,
      fetchError: null,
      localParticipants: []
    }
  },
  computed: {
    columns () {
      if (this.role.toLowerCase() === 'sponsor') {
        return ['Name', 'Email', 'Company Name']
      }
      return ['Name', 'Surname', 'Email', 'Cell Number']
    },
    columnKeys () {
      if (this.role.toLowerCase() === 'sponsor') {
        return {
          Name: 'name',
          Email: 'email',
          'Company Name': 'company_name'
        }
      }
      return {
        Name: 'student_name',
        Surname: 'student_surname',
        Email: 'email',
        'Cell Number': 'cell_number'
      }
    }
  },
  methods: {
    openModal () {
      this.fetchParticipants()
      document.getElementById(this.modalId).showModal()
    },
    closeModal () {
      document.getElementById(this.modalId).close()
    },
    addRow () {
      if (this.role.toLowerCase() === 'sponsor') {
        this.localParticipants.push({ name: '', email: '', company_name: '' })
      } else {
        this.localParticipants.push({ student_name: '', student_surname: '', email: '', cell_number: '' })
      }
    },
    removeRow (index) {
      this.localParticipants.splice(index, 1)
    },
    async fetchParticipants () {
      this.isLoading = true
      this.fetchError = null

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const url = `/cohorts/${this.cohortId}/programmes/${this.programmeId}/participants?role=${encodeURIComponent(this.role)}`

        const response = await fetch(url, {
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken
          }
        })

        if (!response.ok) {
          throw new Error('Failed to load participants')
        }

        const data = await response.json()
        this.localParticipants = data.participants || []
      } catch (err) {
        this.fetchError = err.message
      } finally {
        this.isLoading = false
      }
    },
    async save () {
      this.isSaving = true

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const url = `/cohorts/${this.cohortId}/programmes/${this.programmeId}/participants`

        const response = await fetch(url, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({
            role: this.role,
            participants: this.localParticipants
          })
        })

        if (!response.ok) {
          throw new Error('Failed to save changes')
        }

        // Ensure success
        this.closeModal()
        // Optional: emit event to parent
        this.$emit('saved')
      } catch (err) {
        alert(err.message)
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>
